import React, { useState, useEffect } from 'react';
import { Container, Box } from '@mui/material';
import { LoginForm } from '../components/auth/LoginForm';
import { SignUpForm } from '../components/auth/SignUpForm';
import { ForgotPasswordForm } from '../components/auth/ForgotPasswordForm';
import { getBackgroundImage } from '../api/userApi';

type AuthMode = 'login' | 'signup' | 'forgot-password';

export const AuthPage: React.FC = () => {
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [backgroundUrl, setBackgroundUrl] = useState<string>('/rnm.png');

  const switchToLogin = () => setAuthMode('login');
  const switchToSignUp = () => setAuthMode('signup');
  const switchToForgotPassword = () => setAuthMode('forgot-password');

  // Load background image from API or localStorage
  useEffect(() => {
    const loadBackground = async () => {
      try {
        // First try localStorage for instant loading
        const cachedBackground = localStorage.getItem('broodbook-background');
        if (cachedBackground) {
          setBackgroundUrl(cachedBackground);
        }

        // Then try to get latest from API
        const response = await getBackgroundImage();
        if (response?.data?.data?.backgroundUrl) {
          const newBackgroundUrl = response.data.data.backgroundUrl;
          setBackgroundUrl(newBackgroundUrl);
          localStorage.setItem('broodbook-background', newBackgroundUrl);
        }
      } catch (error) {
        console.warn('AuthPage: Failed to load background, using default');
        // Keep default background on error
      }
    };

    loadBackground();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `url(${backgroundUrl}) center center`,
        backgroundRepeat: 'repeat-x',
        backgroundColor: 'black',
        backgroundSize: 'cover',
        animation: 'moveBackground 60s ease-in-out infinite',
        '@keyframes moveBackground': {
          from: {
            backgroundPosition: '0% 0%',
          },
          to: {
            backgroundPosition: '150% 0%',
          },
        },
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ width: '100%' }}>
          {authMode === 'login' && (
            <LoginForm
              onSwitchToSignUp={switchToSignUp}
              onSwitchToForgotPassword={switchToForgotPassword}
            />
          )}
          {authMode === 'signup' && (
            <SignUpForm onSwitchToLogin={switchToLogin} />
          )}
          {authMode === 'forgot-password' && (
            <ForgotPasswordForm onBackToLogin={switchToLogin} />
          )}
        </Box>
      </Container>
    </Box>
  );
};